import React, { useState } from 'react';
import { adminApi } from '../admin/api';

export const STEPS = [
  { key: 'name', title: 'Nome della struttura', subtitle: 'Come si chiama il tuo B&B o casa vacanze?', icon: 'home' },
  { key: 'logo', title: 'Logo', subtitle: 'Carica il logo della tua struttura (opzionale).', icon: 'image' },
  { key: 'colors', title: 'Colori', subtitle: 'Personalizza la palette del tuo sito.', icon: 'palette' },
  { key: 'contact', title: 'Indirizzo e contatti', subtitle: 'Dove ti trovi e come contattarti.', icon: 'mapPin' },
  { key: 'map', title: 'Mappa', subtitle: 'Posizione geografica della struttura.', icon: 'map' },
  { key: 'checkin', title: 'Check-in', subtitle: 'Orari e istruzioni di accesso.', icon: 'key' },
  { key: 'wifi', title: 'Wi-Fi', subtitle: 'Rete e password per i tuoi ospiti.', icon: 'wifi' },
  { key: 'images', title: 'Immagini', subtitle: 'Aggiungi foto della tua struttura.', icon: 'image' },
  { key: 'preview', title: 'Anteprima', subtitle: 'Verifica le informazioni inserite.', icon: 'eye' },
  { key: 'publish', title: 'Pubblica', subtitle: 'La tua struttura è pronta!', icon: 'rocket' },
];

const inputCls =
  'w-full rounded-lg border border-paelbi-line bg-white px-3 py-2.5 text-sm text-paelbi-ink outline-none focus:border-paelbi-brown';

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-paelbi-ink/80">{label}</span>
      {children}
    </label>
  );
}

export function TextField({ label, value, onChange, placeholder, testid, type = 'text' }) {
  return (
    <Field label={label}>
      <input type={type} value={value || ''} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
    </Field>
  );
}

export function TextArea({ label, value, onChange, placeholder, testid }) {
  return (
    <Field label={label}>
      <textarea value={value || ''} placeholder={placeholder} rows={3}
        onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
    </Field>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-paelbi-line bg-white px-3 py-2">
      <span className="text-sm text-paelbi-ink/80">{label}</span>
      <div className="flex items-center gap-2">
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-24 rounded border border-paelbi-line px-2 py-1 text-xs" data-testid={`${testid}-hex`} />
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-paelbi-line" data-testid={testid} />
      </div>
    </div>
  );
}

function Uploader({ onUploaded, busyLabel = 'Caricamento...', label = 'Carica immagine', testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await adminApi.uploadMedia(file);
      onUploaded(res.url);
    } finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-btn border border-paelbi-brown/40 bg-white px-4 py-2 text-sm font-semibold text-paelbi-ink hover:border-paelbi-brown">
      {busy ? busyLabel : label}
      <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testid} />
    </label>
  );
}

export function StepBody({ stepKey, form, set, tenantSlug }) {
  switch (stepKey) {
    case 'name':
      return (
        <div className="space-y-4">
          <TextField label="Nome struttura" value={form.brandName} testid="wizard-brandname"
            onChange={(v) => set({ brandName: v })} placeholder="Es. Villa Bellavista" />
          <TextField label="Payoff / slogan (opzionale)" value={form.payoff} testid="wizard-payoff"
            onChange={(v) => set({ payoff: v })} placeholder="Es. Il tuo rifugio sul mare" />
        </div>
      );
    case 'logo':
      return (
        <div className="space-y-4">
          {form.logo_url ? (
            <div className="flex items-center gap-4">
              <img src={form.logo_url} alt="logo" className="h-20 w-20 rounded-lg border border-paelbi-line object-contain bg-white p-1" />
              <button type="button" onClick={() => set({ logo_url: null })}
                className="text-sm text-red-600" data-testid="wizard-logo-remove">Rimuovi</button>
            </div>
          ) : <p className="text-sm text-paelbi-ink/60">Nessun logo caricato.</p>}
          <Uploader label="Carica logo" testid="wizard-logo-upload" onUploaded={(url) => set({ logo_url: url })} />
        </div>
      );
    case 'colors':
      return (
        <div className="space-y-3">
          <ColorRow label="Colore principale" value={form.theme.brown} testid="wizard-color-brown"
            onChange={(v) => set({ theme: { ...form.theme, brown: v } })} />
          <ColorRow label="Sfondo" value={form.theme.bg} testid="wizard-color-bg"
            onChange={(v) => set({ theme: { ...form.theme, bg: v } })} />
          <ColorRow label="Sfondo secondario" value={form.theme.bg2} testid="wizard-color-bg2"
            onChange={(v) => set({ theme: { ...form.theme, bg2: v } })} />
          <ColorRow label="Accento" value={form.theme.accent} testid="wizard-color-accent"
            onChange={(v) => set({ theme: { ...form.theme, accent: v } })} />
          <ColorRow label="Testo" value={form.theme.ink} testid="wizard-color-ink"
            onChange={(v) => set({ theme: { ...form.theme, ink: v } })} />
        </div>
      );
    case 'contact':
      return (
        <div className="space-y-4">
          <TextField label="Indirizzo" value={form.address} testid="wizard-address"
            onChange={(v) => set({ address: v })} placeholder="Via Roma 1, Cagliari" />
          <TextField label="Telefono" value={form.phone} testid="wizard-phone"
            onChange={(v) => set({ phone: v })} placeholder="+39 ..." />
          <TextField label="Email pubblica" value={form.email} testid="wizard-pubemail"
            onChange={(v) => set({ email: v })} placeholder="info@tuastruttura.it" />
          <TextField label="Sito web (opzionale)" value={form.website} testid="wizard-website"
            onChange={(v) => set({ website: v })} placeholder="https://..." />
        </div>
      );
    case 'map':
      return (
        <div className="space-y-4">
          <p className="text-sm text-paelbi-ink/60">Inserisci le coordinate della struttura (le trovi su Google Maps).</p>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Latitudine" value={form.lat} testid="wizard-lat" onChange={(v) => set({ lat: v })} />
            <TextField label="Longitudine" value={form.lng} testid="wizard-lng" onChange={(v) => set({ lng: v })} />
          </div>
          <TextField label="Zoom" value={form.zoom} testid="wizard-zoom" onChange={(v) => set({ zoom: v })} />
        </div>
      );
    case 'checkin':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Orario check-in" value={form.checkin_time} testid="wizard-checkin-time"
              onChange={(v) => set({ checkin_time: v })} placeholder="15:00" />
            <TextField label="Orario check-out" value={form.checkout_time} testid="wizard-checkout-time"
              onChange={(v) => set({ checkout_time: v })} placeholder="10:00" />
          </div>
          <TextField label="Codice di accesso (opzionale)" value={form.access_code} testid="wizard-access-code"
            onChange={(v) => set({ access_code: v })} />
          <TextArea label="Istruzioni di accesso" value={form.checkin_instructions} testid="wizard-checkin-instructions"
            onChange={(v) => set({ checkin_instructions: v })} placeholder="Self check-in con codice personale." />
        </div>
      );
    case 'wifi':
      return (
        <div className="space-y-4">
          <TextField label="Nome rete (SSID)" value={form.wifi_ssid} testid="wizard-wifi-ssid"
            onChange={(v) => set({ wifi_ssid: v })} />
          <TextField label="Password Wi-Fi" value={form.wifi_password} testid="wizard-wifi-password"
            onChange={(v) => set({ wifi_password: v })} />
        </div>
      );
    case 'images':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {(form.gallery || []).map((url) => (
              <div key={url} className="group relative overflow-hidden rounded-lg border border-paelbi-line">
                <img src={url} alt="galleria" className="h-24 w-full object-cover" />
                <button type="button" onClick={() => set({ gallery: form.gallery.filter((g) => g !== url) })}
                  className="absolute right-1 top-1 rounded bg-black/60 px-1.5 text-xs text-white">✕</button>
              </div>
            ))}
          </div>
          <Uploader label="Aggiungi foto" testid="wizard-image-upload"
            onUploaded={(url) => set({ gallery: [...(form.gallery || []), url] })} />
        </div>
      );
    case 'preview':
      return (
        <div className="space-y-3 rounded-lg border border-paelbi-line bg-white p-4 text-sm" data-testid="wizard-preview">
          <div className="flex items-center gap-3">
            {form.logo_url && <img src={form.logo_url} alt="logo" className="h-12 w-12 rounded object-contain" />}
            <div>
              <p className="font-display text-lg text-paelbi-ink">{form.brandName || '—'}</p>
              <p className="text-paelbi-ink/60">{form.payoff}</p>
            </div>
          </div>
          <PreviewRow label="Indirizzo" value={form.address} />
          <PreviewRow label="Telefono" value={form.phone} />
          <PreviewRow label="Email" value={form.email} />
          <PreviewRow label="Check-in / out" value={`${form.checkin_time || '—'} / ${form.checkout_time || '—'}`} />
          <PreviewRow label="Wi-Fi" value={form.wifi_ssid} />
          <PreviewRow label="Foto caricate" value={String((form.gallery || []).length)} />
          <div className="flex gap-2 pt-1">
            {['brown', 'bg', 'bg2', 'accent', 'ink'].map((k) => (
              <span key={k} className="h-6 w-6 rounded-full border border-paelbi-line" style={{ background: form.theme[k] }} />
            ))}
          </div>
        </div>
      );
    case 'publish':
      return (
        <div className="space-y-3 text-center" data-testid="wizard-publish">
          <p className="text-base text-paelbi-ink/80">
            Tutto pronto! Pubblica la tua struttura per renderla visibile e accedere alla dashboard.
          </p>
          <p className="text-sm text-paelbi-ink/60">Indirizzo pubblico: <span className="font-mono">/{tenantSlug}</span></p>
        </div>
      );
    default:
      return null;
  }
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-paelbi-line/60 py-1">
      <span className="text-paelbi-ink/60">{label}</span>
      <span className="text-paelbi-ink">{value || '—'}</span>
    </div>
  );
}
